import { mdiClockOutline } from '@mdi/js';
import Icon from '@mdi/react';
import _ from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CoreFetch, MessageApi, MessageLinkEntityType, UserApi } from '../../../api';
import { MessagingContext } from '../../../services';
import { formatDate } from '../../../services/primitives/date-utils';
import { BaseInput, Variables } from '../../base';
import MarkdownWrapper from '../../base/markdown-wrapper/markdown-wrapper';
import {
    AssigneePicker,
    IAssigneeOption,
    assigneeAvatarColor,
    formatAssigneeRole,
} from '../assignee-picker';
import { AttachmentButton } from '../attachment-button';
import { CategoryPicker, ICategoryOption } from '../category-picker';
import { MessagingPill } from '../messaging-badge';
import { authorNickname, isEnterAuthor, isHtmlContent, sanitizeHtml } from '../util';
import { ClaimCard } from './claim-card';
import { CodingReasonCards } from './coding-reason-card';
import {
    formatCommentReasonDisplay,
    getEarliestComment,
    parseCommentReason,
} from './comment-reason.utils';
import { ResolveSection } from './resolve-section';
import * as S from './chat-body.styles';

interface IChatBody {
    hideNote?: boolean;
    hideDone?: boolean;
    onResolveSuccessChange?: (visible: boolean) => void;
}

export const ChatBody: React.FunctionComponent<IChatBody> = (props: IChatBody): React.ReactElement => {
    const { hideNote, onResolveSuccessChange } = props;

    const context = useContext(MessagingContext);
    const message = context.message;
    // Chronologically first comment (by created), not array index 0.
    const comment = getEarliestComment(context.comments);
    const isSupportApp = context.account?.supportGrant === true;
    const nickname = authorNickname(message as any);

    const [isEditable, setEditable] = useState(false);
    const [isSubjectLoading, setSubjectLoading] = useState(false);
    const [subjectView, setSubjectView] = useState(message?.subject || '');
    const [assigneeOptions, setAssigneeOptions] = useState<IAssigneeOption[]>([]);
    const [assigneeLoading, setAssigneeLoading] = useState(false);
    const [assigneeSaving, setAssigneeSaving] = useState(false);
    const [categoryOptions, setCategoryOptions] = useState<ICategoryOption[]>([]);
    const [categoryLoading, setCategoryLoading] = useState(false);
    const [categorySaving, setCategorySaving] = useState(false);

    const currentAccount = context.account?.accountAccess?.find(
        acc => acc.accountId === context.account?.grantedAccountId,
    );
    const isCodingAccessOnly = currentAccount?.roleId === 'CODING_ACCESS';
    const myUserId = context.account?.personalAccountId;
    const canEditLinks = !!context.onLinkEntityToMessage && !!context.onUnlinkEntityFromMessage;
    const canAssignUsers = !isCodingAccessOnly && canEditLinks;
    const canEditCategories = canEditLinks;

    const claimIds = useMemo(() => {
        return _.map(message?.links?.claims, 'id') || [];
    }, [message?.links?.claims]);

    const codingIds = useMemo(() => {
        return _.map(message?.links?.codings, 'id') || [];
    }, [message?.links?.codings]);

    const categoryIds = useMemo(
        () => _.compact(_.map(message?.links?.messageCategories, 'id')),
        [message?.links?.messageCategories],
    );

    const categoryNames = useMemo(() => {
        return categoryIds.map(
            id =>
                categoryOptions.find(o => o.value === id)?.label ||
                context.getCategoryName?.(id) ||
                id,
        );
    }, [categoryIds, categoryOptions, context.getCategoryName]);

    const parsedCommentReason = useMemo(
        () => parseCommentReason(comment?.body),
        [comment?.body],
    );

    const displayBody = useMemo(() => {
        if (parsedCommentReason) {
            return formatCommentReasonDisplay(parsedCommentReason);
        }
        return comment?.body || '';
    }, [parsedCommentReason, comment?.body]);

    const dueDateLabel = useMemo(() => {
        if (!message?.dueDate) return undefined;
        try {
            return formatDate(message.dueDate, 'MMM d, yyyy');
        } catch {
            return undefined;
        }
    }, [message?.dueDate]);

    useEffect(() => {
        if (message?.subject) {
            setSubjectView(message.subject);
        }
    }, [message?.subject]);

    useEffect(() => {
        if (!canAssignUsers) {
            setAssigneeOptions([]);
            return;
        }

        let cancelled = false;
        const loadUsers = async (): Promise<void> => {
            setAssigneeLoading(true);
            try {
                const users = await CoreFetch(UserApi).listProviderUsers({});
                if (cancelled) {
                    return;
                }
                const grantedAccountId = context.account?.grantedAccountId;
                setAssigneeOptions(
                    _.compact(
                        _.map(users, user => {
                            const id = user.personalAccountId;
                            if (!id) {
                                return null;
                            }
                            const name =
                                `${user.first || ''} ${user.last || ''}`.trim() ||
                                user.email ||
                                'Unknown';
                            const roleId = user.roles?.find(r => r.accountId === grantedAccountId)?.roleId;
                            return {
                                id,
                                name,
                                role: formatAssigneeRole(roleId) || user.email,
                                isYou: id === myUserId,
                                color: assigneeAvatarColor(id),
                            } as IAssigneeOption;
                        }),
                    ),
                );
            } catch (e) {
                console.error('Failed to load assignee users:', e);
                if (!cancelled) {
                    setAssigneeOptions([]);
                }
            } finally {
                if (!cancelled) {
                    setAssigneeLoading(false);
                }
            }
        };

        void loadUsers();
        return () => {
            cancelled = true;
        };
    }, [canAssignUsers, context.account?.grantedAccountId, myUserId]);

    const assignedUserIds = useMemo(
        () => _.compact(_.map(message?.links?.users, 'id')),
        [message?.links?.users],
    );
    const selectedAssigneeId = assignedUserIds[0] || null;

    const assigneePickerOptions = useMemo(() => {
        if (!selectedAssigneeId) {
            return assigneeOptions;
        }
        if (assigneeOptions.some(o => o.id === selectedAssigneeId)) {
            return assigneeOptions;
        }
        const fallbackName =
            context.getUserName?.(selectedAssigneeId) || selectedAssigneeId;
        return [
            {
                id: selectedAssigneeId,
                name: fallbackName,
                isYou: selectedAssigneeId === myUserId,
                color: assigneeAvatarColor(selectedAssigneeId),
            },
            ...assigneeOptions,
        ];
    }, [assigneeOptions, selectedAssigneeId, context.getUserName, myUserId]);

    const handleAssign = useCallback(
        async (userId: string | null): Promise<void> => {
            const messageId = message?.id;
            if (
                !messageId ||
                !context.onLinkEntityToMessage ||
                !context.onUnlinkEntityFromMessage ||
                assigneeSaving
            ) {
                return;
            }

            setAssigneeSaving(true);
            try {
                if (assignedUserIds.length > 0) {
                    await context.onUnlinkEntityFromMessage(
                        messageId,
                        MessageLinkEntityType.BANKUSER,
                        assignedUserIds,
                    );
                }
                if (userId) {
                    await context.onLinkEntityToMessage(messageId, MessageLinkEntityType.BANKUSER, [
                        userId,
                    ]);
                }
                context.onMessageFieldsUpdate?.();
            } catch (error) {
                console.error('Failed to update assignee:', error);
            } finally {
                setAssigneeSaving(false);
            }
        },
        [
            message?.id,
            context.onLinkEntityToMessage,
            context.onUnlinkEntityFromMessage,
            context.onMessageFieldsUpdate,
            assignedUserIds,
            assigneeSaving,
        ],
    );

    useEffect(() => {
        if (!canEditCategories) {
            setCategoryOptions([]);
            return;
        }

        let cancelled = false;
        const loadCategories = async (): Promise<void> => {
            setCategoryLoading(true);
            try {
                const categories = isSupportApp
                    ? await CoreFetch(MessageApi).listSupportMessageCategory()
                    : await CoreFetch(MessageApi).listProviderMessageCategory();
                if (cancelled) {
                    return;
                }
                setCategoryOptions(
                    _.map(categories, cat => ({
                        value: cat.id || '',
                        label: cat.name || '',
                    })).filter(opt => !!opt.value),
                );
            } catch (e) {
                console.error('Failed to load message categories:', e);
                if (!cancelled) {
                    setCategoryOptions([]);
                }
            } finally {
                if (!cancelled) {
                    setCategoryLoading(false);
                }
            }
        };

        void loadCategories();
        return () => {
            cancelled = true;
        };
    }, [canEditCategories, isSupportApp]);

    const handleCategoryToggle = useCallback(
        async (categoryId: string): Promise<void> => {
            const messageId = message?.id;
            if (
                !messageId ||
                !context.onLinkEntityToMessage ||
                !context.onUnlinkEntityFromMessage ||
                categorySaving ||
                !categoryId
            ) {
                return;
            }

            const isSelected = categoryIds.includes(categoryId);
            setCategorySaving(true);
            try {
                if (isSelected) {
                    await context.onUnlinkEntityFromMessage(
                        messageId,
                        MessageLinkEntityType.MESSAGECATEGORY,
                        [categoryId],
                    );
                } else {
                    await context.onLinkEntityToMessage(
                        messageId,
                        MessageLinkEntityType.MESSAGECATEGORY,
                        [categoryId],
                    );
                }
                context.onMessageFieldsUpdate?.();
            } catch (error) {
                console.error('Failed to update categories:', error);
            } finally {
                setCategorySaving(false);
            }
        },
        [
            message?.id,
            context.onLinkEntityToMessage,
            context.onUnlinkEntityFromMessage,
            context.onMessageFieldsUpdate,
            categoryIds,
            categorySaving,
        ],
    );

    // ResolveSection is only for ENTER task authors — clear Success ownership otherwise.
    useEffect(() => {
        if (!message || !isEnterAuthor(message as any)) {
            onResolveSuccessChange?.(false);
        }
    }, [message, onResolveSuccessChange]);

    if (!context.message) {
        return <></>;
    }

    const hasNote = !hideNote && !!comment && _.get(comment, 'note', false);
    const { id } = message;

    const commentDocIds = (_.get(comment, 'documents', []) || []) as string[];
    const documents = _.filter(context.documents, document => {
        return _.includes(commentDocIds, document.id);
    });

    const isDocumentsLoading = commentDocIds.length > 0 && documents.length === 0;

    const renderAttachments = (): React.ReactElement | React.ReactElement[] | undefined => {
        if (isDocumentsLoading) {
            return <S.LoadingText>Loading documents...</S.LoadingText>;
        }
        if (!documents || !documents.length) {
            return;
        }
        return _.times(_.size(documents), (i: number): React.ReactElement => {
            const document = documents[i];
            const onDownload = async (): Promise<void> => {
                if (document && document.id) {
                    await (context.onDocumentDownload && context.onDocumentDownload(document.id));
                }
            };
            const onActualDownload = async (): Promise<string | void> => {
                if (document && document.id && context.onDocumentActualDownload) {
                    return await context.onDocumentActualDownload(document.id);
                }
            };

            const onAssign = async (): Promise<void> => {
                await (context.onDocumentAssign && context.onDocumentAssign(document));
            };

            const onEdit = async (): Promise<void> => {
                await (context.onDocumentEdit && context.onDocumentEdit(document));
            };

            const canAssign = claimIds.length > 0 && !document.claimId && !!context.onDocumentAssign;

            return (
                <AttachmentButton
                    key={`attachment-${i}`}
                    filename={document.filename as string}
                    filesize={document.filesize}
                    thumbnail={document.thumbnail}
                    onDownload={onDownload}
                    onActualDownload={
                        context.onDocumentActualDownload ? onActualDownload : undefined
                    }
                    onAssign={canAssign ? onAssign : undefined}
                    onEdit={context.onDocumentEdit ? onEdit : undefined}
                    isSupportApp={isSupportApp}
                />
            );
        });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSubjectView(e.target.value);
    };

    const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
        setSubjectView(e.target.value);
        if (!id) {
            throw new Error("Can't edit subject for this message");
        }
        setSubjectLoading(true);
        await context.onSubjectUpdate(id, e.target.value);
        setSubjectLoading(false);
        handleToggleEditable();
    };

    const handleToggleEditable = () => {
        if (!context.isSubjectEditable) {
            return;
        }

        setEditable(!isEditable);
    };

    const hasAiPill = isEnterAuthor(message as any);
    const hasNotePill = !hideNote && hasNote;

    return (
        <S.Header note={hasNote}>
            {hasAiPill && (
                <S.Row>
                    <MessagingPill>AI Message</MessagingPill>
                </S.Row>
            )}
            <S.SubjectRow>
                {isEditable ? (
                    <S.EditableSubject title="Edit">
                        <BaseInput
                            onChange={handleChange}
                            disabled={isSubjectLoading}
                            onBlur={handleBlur}
                            value={subjectView}
                            autoFocus={true}
                        />
                    </S.EditableSubject>
                ) : (
                    <S.Subject onClick={handleToggleEditable}>{subjectView}</S.Subject>
                )}
                {dueDateLabel && (
                    <S.DueDate>
                        <Icon path={mdiClockOutline} size={0.6} color={Variables.ThemeLight.AttentionConstant6} />
                        Due {dueDateLabel}
                    </S.DueDate>
                )}
                {hasNotePill && (
                    <S.SubjectPills>
                        <MessagingPill note={true}>Internal Note</MessagingPill>
                    </S.SubjectPills>
                )}
                {(canEditCategories || canAssignUsers) && (
                    <S.AssigneeSlot>
                        {canEditCategories && (
                            <CategoryPicker
                                selectedIds={categoryIds}
                                options={categoryOptions}
                                loading={categoryLoading}
                                saving={categorySaving}
                                onToggle={handleCategoryToggle}
                            />
                        )}
                        {canAssignUsers && (
                            <AssigneePicker
                                selectedId={selectedAssigneeId}
                                options={assigneePickerOptions}
                                loading={assigneeLoading}
                                saving={assigneeSaving}
                                onAssign={handleAssign}
                            />
                        )}
                    </S.AssigneeSlot>
                )}
            </S.SubjectRow>
            <S.MessageGrid>
                <S.Body>
                    {isHtmlContent(displayBody) ? (
                        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(displayBody) }} />
                    ) : (
                        <MarkdownWrapper text={displayBody} />
                    )}
                </S.Body>
                <S.Attachments>{renderAttachments()}</S.Attachments>
            </S.MessageGrid>
            <S.ContentSections>
                <ClaimCard claimIds={claimIds} messageId={id} />
                <CodingReasonCards
                    codingIds={codingIds}
                    isSupportApp={isSupportApp}
                    subject={message.subject}
                    categoryNames={categoryNames}
                    commentBody={comment?.body}
                    messageCreated={message.created}
                />
                {isEnterAuthor(message as any) && (
                    <ResolveSection
                        claimIds={claimIds}
                        messageId={context.message.id}
                        onSuccessVisibilityChange={onResolveSuccessChange}
                    />
                )}
            </S.ContentSections>
        </S.Header>
    );
};
